// src/components/resources/quizzes/components/QuizCompletion.tsx

'use client'

import { logClientError } from '~/lib/logger-client'
import { useState } from 'react'
import { useIsomorphicLayoutEffect } from '~/lib/hooks/useIsomorphicLayoutEffect'
import { cn } from '~/lib/utils'
import RiMailLine from '~/icons/remix/generated/RiMailLine'
import RiArrowRightLine from '~/icons/remix/generated/RiArrowRightLine'
import RiBarChartLine from '~/icons/remix/generated/RiBarChartLine'
import RiMedalLine from '~/icons/remix/generated/RiMedalLine'
import RiBookOpenLine from '~/icons/remix/generated/RiBookOpenLine'
import RiLightbulbLine from '~/icons/remix/generated/RiLightbulbLine'
import { EmbeddedEmailCapture } from '~/components/forms'
import { QuizRecommendations, getResourceIconForSupplemental } from '~/components/resources/quizzes/components/QuizRecommendations'
import type { Quiz, QuizResult, RecommendationItem } from '~/lib/resources/types/tools/quiz'
import { getUrlFromSupplementalResource, isExternalSupplementalResource } from '~/lib/resources/utils/resourceUtils'

interface QuizCompletionProps {
  quiz: Quiz
  result: QuizResult
  onRetake?: () => void
}

export function QuizCompletion({ quiz, result, onRetake }: QuizCompletionProps): JSX.Element {
  const [showEmailCapture, setShowEmailCapture] = useState(false)

  // Cache Components: this email-capture modal shows a post-submit success
  // state. <Activity> would preserve it open across navigation, so a user
  // returning to the completed-quiz route would be greeted by a stale, modal
  // "results sent" overlay. Close it when the route is hidden.
  useIsomorphicLayoutEffect(() => {
    return () => setShowEmailCapture(false)
  }, [])

  // Get achievement level from result
  const achievementLevel = result.achievement

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 px-8 py-16 text-center shadow-lg dark:from-indigo-400/20 dark:via-purple-400/20 dark:to-pink-400/20">
        <div className="absolute inset-0 bg-grid-gray-900/[0.03] [mask-image:linear-gradient(0deg,transparent,black)] dark:bg-grid-white/[0.03]" />
        
        <div className="relative">
          <div className="mx-auto mb-10 flex max-w-sm flex-col items-center">
            <div className="relative mb-8">
              <div className="absolute inset-0 animate-pulse-slow rounded-full bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 blur-2xl dark:from-indigo-400/20 dark:via-purple-400/20 dark:to-pink-400/20" />
              <div className="relative flex h-40 w-40 items-center justify-center rounded-full bg-gradient-to-br from-white via-gray-50 to-gray-100 shadow-2xl ring-1 ring-gray-900/10 dark:from-gray-800 dark:via-gray-900 dark:to-black dark:ring-white/10">
                <div className="flex flex-col items-center">
                  <RiMedalLine className={cn(
                    "mb-1 h-8 w-8",
                    result.passed ? "text-green-500 dark:text-green-400" : "text-orange-500 dark:text-orange-400"
                  )} />
                  <span className={cn(
                    "text-6xl font-extrabold tracking-tight",
                    result.passed 
                      ? "bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 bg-clip-text text-transparent dark:from-green-400 dark:via-emerald-400 dark:to-teal-400"
                      : "bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 bg-clip-text text-transparent dark:from-orange-400 dark:via-amber-400 dark:to-yellow-400"
                  )}>
                    {result.score}%
                  </span>
                </div>
              </div>
            </div>

            {achievementLevel && (
              <div className="transform rounded-full bg-gradient-to-r from-indigo-100 via-white to-pink-100 px-6 py-2 shadow-md ring-1 ring-gray-900/10 dark:from-indigo-900/40 dark:via-gray-800 dark:to-pink-900/40 dark:ring-white/10">
                <div className="flex items-center gap-3">
                  <span className={cn(
                    "h-2.5 w-2.5 rounded-full",
                    achievementLevel === 'expert' ? "bg-green-500" :
                    achievementLevel === 'advanced' ? "bg-blue-500" :
                    achievementLevel === 'intermediate' ? "bg-yellow-500" :
                    achievementLevel === 'beginner' ? "bg-gray-500" :
                    "bg-red-500"
                  )} />
                  <span className="bg-gradient-to-br from-gray-900 to-gray-700 bg-clip-text text-lg font-bold text-transparent dark:from-white dark:to-gray-300">
                    {achievementLevel.charAt(0).toUpperCase() + achievementLevel.slice(1)} Level
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* H1 is provided by quiz cover */}
          <h2 className="mb-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-4xl font-extrabold tracking-tight text-transparent dark:from-white dark:via-gray-100 dark:to-gray-200">
            {result.passed ? 'Outstanding Achievement!' : 'Keep Growing!'}
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-400">
            {result.feedback.summary}
          </p>
        </div>
      </div>

      {/* Section Performance */}
      <div className="overflow-hidden rounded-2xl bg-white shadow-lg ring-1 ring-gray-900/5 dark:bg-gray-800/80 dark:ring-white/10">
        <div className="border-b border-gray-900/10 px-6 py-5 dark:border-white/10">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-indigo-100 p-2 dark:bg-indigo-900/30">
              <RiBookOpenLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Section Performance
            </h3>
          </div>
        </div>
        
        <div className="divide-y divide-gray-900/5 px-6 dark:divide-white/5">
          {Object.entries(result.sectionResults).map(([sectionId, score], index) => {
            const section = quiz.sections.find(s => s.id === sectionId)
            if (!section) return null

            // Find matching section trigger based on score with proper type checks
            const sectionTrigger = quiz.config?.results?.sectionTriggers?.[sectionId]?.find(
              trigger => score.percentage >= (trigger.minScore ?? 0) && score.percentage <= (trigger.maxScore ?? 100)
            )

            return (
              <div key={sectionId} className="py-6">
                {/* Score Header */}
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-lg text-sm font-bold",
                      score.percentage >= 80
                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                        : score.percentage >= 60
                        ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400"
                        : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
                    )}>
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {section.title}
                      </h4>
                      <div className="mt-1 flex items-center gap-3">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {score.score} of {score.total} correct
                        </p>
                        <div className={cn(
                          "text-lg font-bold",
                          score.percentage >= 80
                            ? "text-green-600 dark:text-green-400"
                            : score.percentage >= 60
                            ? "text-yellow-600 dark:text-yellow-400"
                            : "text-red-600 dark:text-red-400"
                        )}>
                          {score.percentage}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress and Content */}
                <div className="space-y-6">
                  {/* Progress Bar */}
                  <div className="relative h-2.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                    <div
                      className={cn(
                        "absolute inset-y-0 left-0 rounded-full transition-all duration-500",
                        score.percentage >= 80
                          ? "bg-green-500 dark:bg-green-400"
                          : score.percentage >= 60
                          ? "bg-yellow-500 dark:bg-yellow-400"
                          : "bg-red-500 dark:bg-red-400"
                      )}
                      style={{ width: `${score.percentage}%` }}
                    />
                  </div>

                  {/* Main Content Grid */}
                  <div className="grid gap-6 lg:grid-cols-2">
                    {/* Left Column: Objectives and Feedback */}
                    <div className="space-y-4">
                      {/* Learning Objectives */}
                      {section.objectives && section.objectives.length > 0 && (
                        <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/5">
                          <h5 className="mb-3 font-medium text-gray-900 dark:text-white">
                            Learning Objectives
                          </h5>
                          <ul className="space-y-2">
                            {section.objectives.map((objective, i) => (
                              <li
                                key={i}
                                className="flex items-start gap-3 text-sm text-gray-600 dark:text-gray-400"
                              >
                                <div className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-gray-400 dark:bg-gray-500" />
                                {objective}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Feedback */}
                      {sectionTrigger?.feedback && (
                        <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/5">
                          <h5 className="mb-3 font-medium text-gray-900 dark:text-white">
                            Feedback
                          </h5>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {sectionTrigger.feedback}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Right Column: Recommendations and Next Steps */}
                    <div className="space-y-4">
                      {/* Areas for Improvement */}
                      {sectionTrigger?.areasForImprovement && sectionTrigger.areasForImprovement.length > 0 && (
                        <div className="rounded-lg bg-orange-50 p-4 shadow-sm ring-1 ring-orange-900/10 dark:bg-orange-900/20 dark:ring-orange-400/10">
                          <h6 className="font-medium text-orange-900 dark:text-orange-300">
                            Areas for Improvement
                          </h6>
                          <ul className="mt-2 space-y-2">
                            {sectionTrigger.areasForImprovement.map((area, i) => (
                              <li key={i} className="text-sm text-orange-800 dark:text-orange-200">
                                • {area}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Recommendations */}
                      {sectionTrigger?.recommendations && sectionTrigger.recommendations.length > 0 && (
                        <div className="rounded-lg bg-indigo-50 p-4 shadow-sm ring-1 ring-indigo-900/10 dark:bg-indigo-900/20 dark:ring-indigo-400/10">
                          <h6 className="font-medium text-indigo-900 dark:text-indigo-300">
                            Recommended Resources
                          </h6>
                          <ul className="mt-2 space-y-3">
                            {sectionTrigger.recommendations.map((resource: RecommendationItem, i) => {
                              if (typeof resource === 'string') {
                                return (
                                  <li key={i} className="text-sm text-indigo-800 dark:text-indigo-200">
                                    • {resource}
                                  </li>
                                )
                              }

                              return (
                                <li key={i} className="text-sm">
                                  <a
                                    href={getUrlFromSupplementalResource(resource)}
                                    target={isExternalSupplementalResource(resource) ? "_blank" : undefined}
                                    rel={isExternalSupplementalResource(resource) ? "noopener noreferrer" : undefined}
                                    className="group flex items-start gap-3 rounded-lg p-2 transition-colors hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
                                  >
                                    <div className="mt-1 flex-none rounded bg-indigo-100 p-1.5 text-indigo-600 group-hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-400 dark:group-hover:bg-indigo-900/60">
                                      {getResourceIconForSupplemental(resource, { className: "h-4 w-4 transition-transform group-hover:translate-x-0.5" })}
                                    </div>
                                    <div>
                                      <div className="font-medium text-indigo-900 group-hover:text-indigo-700 dark:text-indigo-300 dark:group-hover:text-indigo-200">
                                        {resource.title}
                                      </div>
                                      {resource.description && (
                                        <p className="text-xs text-indigo-700 dark:text-indigo-400">
                                          {resource.description}
                                        </p>
                                      )}
                                    </div>
                                  </a>
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      )}

                      {/* Next Steps */}
                      {sectionTrigger?.nextSteps && sectionTrigger.nextSteps.length > 0 && (
                        <div className="rounded-lg bg-emerald-50 p-4 shadow-sm ring-1 ring-emerald-900/10 dark:bg-emerald-900/20 dark:ring-emerald-400/10">
                          <h6 className="font-medium text-emerald-900 dark:text-emerald-300">
                            Next Steps
                          </h6>
                          <ul className="mt-2 space-y-2">
                            {sectionTrigger.nextSteps.map((step, i) => (
                              <li key={i} className="text-sm text-emerald-800 dark:text-emerald-200">
                                • {step}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Strengths & Areas for Improvement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {result.feedback.strengths.length > 0 && (
          <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-green-50 to-emerald-50/50 p-6 shadow-lg ring-1 ring-green-900/5 dark:from-green-900/20 dark:to-emerald-900/10 dark:ring-green-400/5">
            <h4 className="flex items-center gap-3 text-xl font-semibold text-green-900 dark:text-green-300">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
                <RiBarChartLine className="h-5 w-5 text-green-600 dark:text-green-300" />
              </span>
              Your Strengths
            </h4>
            <ul className="mt-4 space-y-4">
              {result.feedback.strengths.map((strength, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="mt-1.5 h-2 w-2 flex-none rounded-full bg-green-600 dark:bg-green-400" />
                  <span className="text-sm text-green-800 dark:text-green-200">
                    {strength}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result.feedback.areasForImprovement.length > 0 && (
          <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50/50 p-6 shadow-lg ring-1 ring-orange-900/5 dark:from-orange-900/20 dark:to-amber-900/10 dark:ring-orange-400/5">
            <h4 className="flex items-center gap-3 text-xl font-semibold text-orange-900 dark:text-orange-300">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/50">
                <RiLightbulbLine className="h-5 w-5 text-orange-600 dark:text-orange-300" />
              </span>
              Areas for Improvement
            </h4>
            <ul className="mt-4 space-y-4">
              {result.feedback.areasForImprovement.map((area, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="mt-1.5 h-2 w-2 flex-none rounded-full bg-orange-600 dark:bg-orange-400" />
                  <span className="text-sm text-orange-800 dark:text-orange-200">
                    {area}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Recommendations */}
      <QuizRecommendations result={result} />

      {/* Action Buttons */}
      <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
        <button
          onClick={() => setShowEmailCapture(true)}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 text-base font-medium text-indigo-600 shadow-lg ring-1 ring-indigo-900/10 transition hover:bg-indigo-50 sm:w-auto dark:bg-indigo-900/30 dark:text-indigo-300 dark:ring-indigo-400/20 dark:hover:bg-indigo-900/40"
        >
          <RiMailLine className="h-5 w-5" />
          Email Results
        </button>

        {quiz.config.allowRetake && onRetake && (
          <button
            onClick={onRetake}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 text-base font-medium text-gray-700 shadow-lg ring-1 ring-gray-900/10 transition hover:bg-gray-50 sm:w-auto dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-700"
          >
            <RiArrowRightLine className="h-5 w-5" />
            Retake Quiz
          </button>
        )}
      </div>

      {/* Email Capture Modal */}
      {showEmailCapture && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 md:p-20">
          <div className="fixed inset-0 bg-gray-500/75 dark:bg-gray-900/90 transition-opacity" />
          <div className="relative w-full max-w-xl transform rounded-xl bg-white p-6 text-left shadow-xl transition-all dark:bg-gray-800 sm:p-8">
            <EmbeddedEmailCapture
              preset="card"
              content={{
                title: {
                  text: `${quiz.title} - Your Results`,
                  className: "text-xl font-semibold text-gray-900 dark:text-white"
                },
                description: {
                  text: "Enter your email to receive your detailed quiz results and personalized recommendations.",
                  className: "text-sm text-gray-600 dark:text-gray-400"
                },
                button: {
                  text: "Send Results",
                  loadingText: "Sending...",
                  icon: <RiMailLine className="h-4 w-4" />
                },
                messages: {
                  success: "Your quiz results have been sent!",
                  error: "Failed to send results. Please try again."
                },
                privacy: {
                  enabled: true,
                  text: "By continuing, you agree to our",
                  className: "text-xs text-gray-500 dark:text-gray-400"
                }
              }}
              template={{
                templateAlias: "resource-center-tool-results",
                to: '', // Will be set by BaseEmailCaptureForm
                from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
                templateModel: {
                  base: {
                    name: '',
                    logo_url: process.env.NEXT_PUBLIC_LOGO_URL ?? 'https://adsights.ai/logo-square.png',
                    product_name: process.env.NEXT_PUBLIC_PRODUCT_NAME ?? 'AdSights',
                    product_url: process.env.NEXT_PUBLIC_PRODUCT_URL ?? 'https://www.adsights.ai',
                    support_email: process.env.NEXT_PUBLIC_SUPPORT_EMAIL ?? 'support@adsights.ai',
                    sender_name: process.env.NEXT_PUBLIC_SENDER_NAME ?? 'The AdSights Team',
                    company_name: process.env.NEXT_PUBLIC_COMPANY_NAME ?? 'AdSights Inc.',
                    company_address: process.env.NEXT_PUBLIC_COMPANY_ADDRESS ?? '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702'
                  },
                  custom: {
                    quiz: {
                      title: quiz.title,
                      description: quiz.description
                    },
                    result: {
                      score: result.score,
                      passed: result.passed,
                      achievement: result.achievement,
                      feedback: result.feedback,
                      sectionResults: result.sectionResults
                    }
                  }
                }
              }}
              analytics={{
                enabled: true,
                category: "quiz",
                label: quiz.slug,
                elementId: "quiz_results_email_capture"
              }}
              onSuccess={() => {
                setShowEmailCapture(false)
              }}
              onError={(error) => {
                logClientError(error, { component: 'QuizCompletion', context: 'Email capture error' })
              }}
            />
            
            {/* Close Button */}
            <button
              type="button"
              className="absolute right-4 top-4 rounded-lg p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:text-gray-500 dark:hover:text-gray-400"
              onClick={() => setShowEmailCapture(false)}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}